import React from 'react';
import {Link} from 'react-router-dom';
import {
  UploadCloudIcon,
  WandIcon,
  FileCodeIcon,
  ListIcon,
  FolderIcon,
  ZapIcon,
  PlayCircleIcon,
  ExternalLinkIcon,
} from '../Icons';
import VideoEditorPicker from '../VideoEditorPicker';
import type {VideoEditor} from '../../backend-types/user';
import {WALKTHROUGH_VIDEO_URL, WALKTHROUGH_VIDEO_LENGTH} from '../../domain/videos';

/** Same semantics as the header pill: emerald = fine, amber = low, red = spent. */
const QUOTA_TONE = {
  ok: 'text-emerald-300',
  low: 'text-amber-200',
  critical: 'text-red-200',
} as const;

interface WelcomeGuideProps {
  displayName: string;
  /** The transcription balance, already formatted. Null until a usage read has landed. */
  quotaLabel: string | null;
  quotaLevel: keyof typeof QUOTA_TONE;
  planLabel: string;
  workspaceHref: string;
  wordlistsHref: string;
  /** False once the page has already been turned off — the visit is an explicit `?show=1` one. */
  canDismiss: boolean;
  isDismissing: boolean;
  onGetStarted: () => void;
  onDismiss: () => void;
  videoEditor: VideoEditor | null;
  onSelectVideoEditor: (editor: VideoEditor) => void;
  isSavingVideoEditor: boolean;
}

const STEPS = [
  {
    icon: UploadCloudIcon,
    title: 'Upload your audio',
    body: 'Drop an audio file into the workspace. It uploads straight to storage, so a '
      + 'full-length episode is fine.',
    note: 'Free and unlimited',
  },
  {
    icon: WandIcon,
    title: 'Transcribe it',
    body: 'One click turns the file into a timestamped CSV of every spoken word. Keep working '
      + 'while it runs — the header tracks the job.',
    note: 'The only step that spends time',
  },
  {
    icon: FileCodeIcon,
    title: 'Convert to XML',
    body: 'Pick a word list and get a Final Cut Pro XML with every match cut out. Run it again '
      + 'with a different list whenever you want.',
    note: 'Free and repeatable',
  },
] as const;

/**
 * The first screen after signing in: what the app does, in the order the user will do it.
 *
 * It mirrors the workspace deliberately — the three steps here are the three things a row in that
 * table can be, so the page reads as a preview of the screen it hands you off to rather than as
 * marketing copy with its own visual language.
 */
const WelcomeGuide: React.FC<WelcomeGuideProps> = ({
  displayName,
  quotaLabel,
  quotaLevel,
  planLabel,
  workspaceHref,
  wordlistsHref,
  canDismiss,
  isDismissing,
  onGetStarted,
  onDismiss,
  videoEditor,
  onSelectVideoEditor,
  isSavingVideoEditor,
}) => (
  <div className="h-full overflow-y-auto bg-gray-950">
    <div className="mx-auto max-w-4xl px-4 py-8 space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-gray-50">
          Welcome to CensorMe{displayName ? `, ${displayName}` : ''}
        </h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Turn spoken audio into a Final Cut Pro XML with every word on your list cut out.
        </p>
      </header>

      <section className="overflow-hidden rounded-xl border border-indigo-500/30 bg-indigo-500/[0.06]">
        <a
          href={WALKTHROUGH_VIDEO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-start gap-4 p-4 transition-colors hover:bg-indigo-500/[0.06]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl
           bg-indigo-600 text-white transition-colors group-hover:bg-indigo-500">
            <PlayCircleIcon className="h-6 w-6" />
          </span>

          <div className="min-w-0">
            <h2 className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold text-gray-50">
              Watch the walkthrough
              <span className="text-xs font-normal tabular-nums text-indigo-300/70">
                {WALKTHROUGH_VIDEO_LENGTH}
              </span>
            </h2>
            <p className="mt-1 text-sm text-gray-400">
              The whole workflow in one video: uploading your audio, transcribing it, building a
              word list and converting to XML — then importing the timeline into Premiere Pro and
              DaVinci Resolve, pointing either of them back at your source audio when it opens
              offline, and using Mapper.
            </p>
            <p className="mt-2 text-xs text-gray-500">Opens in a new tab</p>
          </div>

          <ExternalLinkIcon className="mt-1 ml-auto hidden h-4 w-4 shrink-0 text-indigo-400 sm:block" />
        </a>
      </section>

      <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-100">How it works</h2>
          {quotaLabel && (
            <span className="text-xs text-gray-500">
              <span className="text-gray-600">{planLabel} plan · </span>
              <span className={`font-medium tabular-nums ${QUOTA_TONE[quotaLevel]}`}>{quotaLabel}</span>
              <span className="text-gray-600"> of transcription time left</span>
            </span>
          )}
        </header>

        <ol className="grid gap-px bg-gray-800 md:grid-cols-3">
          {STEPS.map((step, index) => (
            <li key={step.title} className="bg-gray-900/60 p-4">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg border
                 border-gray-800 bg-gray-950 text-xs font-medium tabular-nums text-gray-400">
                  {index + 1}
                </span>
                <step.icon className="h-4 w-4 text-indigo-400" />
                <h3 className="text-sm font-medium text-gray-100">{step.title}</h3>
              </div>
              <p className="mt-2 text-sm text-gray-400">{step.body}</p>
              <p className="mt-2 text-xs text-gray-600">{step.note}</p>
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap items-center gap-2 border-t border-gray-800 px-4 py-3">
          <button
            type="button"
            onClick={onGetStarted}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm
             font-medium text-white hover:bg-indigo-500 transition-colors cursor-pointer"
          >
            <UploadCloudIcon className="h-4 w-4" />
            Open the workspace
          </button>

          {canDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              disabled={isDismissing}
              title="Go straight to the workspace next time you sign in"
              className="inline-flex items-center gap-2 rounded-lg border border-gray-800 px-3 py-1.5
               text-sm font-medium text-gray-300 hover:border-gray-700 hover:bg-gray-800
               transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {isDismissing && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-[1.5px] border-current
                 border-t-transparent" />
              )}
              {isDismissing ? 'Saving…' : 'Don’t show this again'}
            </button>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
        <header className="border-b border-gray-800 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-100">Which editor do you cut in?</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Optional, and you can change it any time in Settings. It tailors what the convert dialog
            tells you about linking your source audio after import — every editor handles a media
            path it can&rsquo;t find a little differently.
          </p>
        </header>
        <div className="p-4">
          <VideoEditorPicker value={videoEditor} onSelect={onSelectVideoEditor} disabled={isSavingVideoEditor} />
        </div>
      </section>

      <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
        <header className="border-b border-gray-800 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-100">Worth setting up first</h2>
        </header>

        <div className="divide-y divide-gray-800">
          <WelcomeLink
            to={wordlistsHref}
            icon={ListIcon}
            title="Build a word list"
            body="The words a conversion cuts. Create your own, import a .csv or .txt, or add a
             ready-made list from the marketplace."
          />
          <WelcomeLink
            to="/projects"
            icon={FolderIcon}
            title="Organise with projects"
            body="Each project keeps its own audio, transcriptions and default word list. Everything
             lands in your Default Project until you make another."
          />
          <WelcomeLink
            to="/account"
            icon={ZapIcon}
            title="Check your plan"
            body="Transcription is metered by audio length and resets every month. Conversions never
             cost anything."
          />
        </div>
      </section>

      <p className="max-w-2xl px-1 text-xs text-gray-600">
        You can come back to this page any time from{' '}
        <span className="text-gray-500">Getting started</span> in the profile menu, even after you
        hide it — or go straight to{' '}
        <Link to={workspaceHref} className="text-gray-500 hover:text-gray-400">the workspace</Link>
        {' '}and start with a file.
      </p>
    </div>
  </div>
);

const WelcomeLink: React.FC<{
  to: string;
  icon: React.FC<{className?: string}>;
  title: string;
  body: string;
}> = ({to, icon: Icon, title, body}) => (
  <Link to={to} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-800/40 transition-colors">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-100">{title}</p>
      <p className="mt-0.5 text-sm text-gray-500">{body}</p>
    </div>
    <span className="ml-auto shrink-0 self-center text-xs text-indigo-400">→</span>
  </Link>
);

export default WelcomeGuide;
